import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Business,
  CheckCircle,
  Error,
  Schedule,
  Refresh,
  People,
  Groups
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { Tenant, TenantDeployment } from '../types';
import StatusChip from '../components/StatusChip';
import LoadingButton from '../components/LoadingButton';

interface DashboardStats {
  totalTenants: number;
  activeTenants: number;
  failedTenants: number;
  totalResellers: number;
  totalCustomers: number;
  pendingDeployments: number;
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats>({
    totalTenants: 0,
    activeTenants: 0,
    failedTenants: 0,
    totalResellers: 0,
    totalCustomers: 0,
    pendingDeployments: 0
  });
  const [recentTenants, setRecentTenants] = useState<Tenant[]>([]);
  const [recentDeployments, setRecentDeployments] = useState<TenantDeployment[]>([]);
  const [customersByReseller, setCustomersByReseller] = useState<Array<{ reseller_name: string; customer_count: number }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const loadDashboardData = async () => {
    try {
      setError('');
      
      // Load tenants
      const tenantsResponse = await apiService.getTenants({ per_page: 10 });
      const tenants = tenantsResponse.tenants;
      setRecentTenants(tenants);

      // Calculate stats
      const totalTenants = tenantsResponse.total;
      const activeTenants = tenants.filter(t => t.status === 'active').length;
      const failedTenants = tenants.filter(t => t.status === 'failed').length;

      // Load recent deployments
      const deploymentsResponse = await apiService.getAllDeployments({ per_page: 10 });
      const deployments = deploymentsResponse.deployments;
      setRecentDeployments(deployments);

      const pendingDeployments = deployments.filter(d => 
        d.status === 'pending' || d.status === 'in_progress'
      ).length;

      // Load customers and resellers data (only for super admin)
      let totalCustomers = 0;
      let totalResellers = 0;
      let resellerStats: Array<{ reseller_name: string; customer_count: number }> = [];
      
      if (user?.role === 'super_admin') {
        try {
          const [customersResponse, usersResponse] = await Promise.all([
                  apiService.getCustomers(0, 100), // Get customers (max 100)
      apiService.getUsers(0, 100) // Get users (max 100)
          ]);
          
          totalCustomers = customersResponse.total;
          totalResellers = usersResponse.length;
          
          // Calculate customers per reseller
          const resellerMap = new Map<string, number>();
          
          // Group customers by their tenants' owners (resellers)
          const allTenants = await apiService.getTenants({ per_page: 100 });
          allTenants.tenants.forEach(tenant => {
            const ownerUser = usersResponse.find(u => u.id === tenant.owner_id);
            if (ownerUser && ownerUser.reseller_name) {
              const currentCount = resellerMap.get(ownerUser.reseller_name) || 0;
              resellerMap.set(ownerUser.reseller_name, currentCount + 1);
            }
          });
          
          resellerStats = Array.from(resellerMap.entries())
            .map(([reseller_name, customer_count]) => ({ reseller_name, customer_count }))
            .sort((a, b) => b.customer_count - a.customer_count)
            .slice(0, 5); // Top 5 resellers
          
        } catch (error) {
          console.error('Error loading reseller/customer stats:', error);
        }
      }
      
      setCustomersByReseller(resellerStats);

      setStats({
        totalTenants,
        activeTenants,
        failedTenants,
        totalResellers,
        totalCustomers,
        pendingDeployments
      });

    } catch (err: any) {
      console.error('Failed to load dashboard data:', err);
      setError(err.detail || t('dashboard.errorLoadingData'));
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('dashboard.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('dashboard.welcomeBack', { email: user?.email })}
          </Typography>
        </div>
        <LoadingButton
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          loading={refreshing}
          loadingText={t('dashboard.refreshData')}
        >
          {t('dashboard.refreshData')}
        </LoadingButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Business color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <div>
                  <Typography color="text.secondary" gutterBottom>
                    {t('dashboard.totalTenants')}
                  </Typography>
                  <Typography variant="h5">
                    {stats.totalTenants}
                  </Typography>
                </div>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircle color="success" sx={{ fontSize: 40, mr: 2 }} />
                <div>
                  <Typography color="text.secondary" gutterBottom>
                    {t('dashboard.activeTenants')}
                  </Typography>
                  <Typography variant="h5">
                    {stats.activeTenants}
                  </Typography>
                </div>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Error color="error" sx={{ fontSize: 40, mr: 2 }} />
                <div>
                  <Typography color="text.secondary" gutterBottom>
                    {t('dashboard.failedTenants')}
                  </Typography>
                  <Typography variant="h5">
                    {stats.failedTenants}
                  </Typography>
                </div>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Schedule color="warning" sx={{ fontSize: 40, mr: 2 }} />
                <div>
                  <Typography color="text.secondary" gutterBottom>
                    {t('dashboard.pendingDeployments')}
                  </Typography>
                  <Typography variant="h5">
                    {stats.pendingDeployments}
                  </Typography>
                </div>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Reseller and Customer stats - only for super admin */}
        {user?.role === 'super_admin' && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <People color="info" sx={{ fontSize: 40, mr: 2 }} />
                    <div>
                      <Typography color="text.secondary" gutterBottom>
                        {t('dashboard.totalResellers')}
                      </Typography>
                      <Typography variant="h5">
                        {stats.totalResellers}
                      </Typography>
                    </div>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Groups color="secondary" sx={{ fontSize: 40, mr: 2 }} />
                    <div>
                      <Typography color="text.secondary" gutterBottom>
                        {t('dashboard.totalCustomers')}
                      </Typography>
                      <Typography variant="h5">
                        {stats.totalCustomers}
                      </Typography>
                    </div>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Customers per Reseller - only for super admin */}
      {user?.role === 'super_admin' && customersByReseller.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('dashboard.customersPerReseller')}
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Reseller</TableCell>
                        <TableCell align="right">Clientes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customersByReseller.map((reseller, index) => (
                        <TableRow key={index}>
                          <TableCell>{reseller.reseller_name}</TableCell>
                          <TableCell align="right">{reseller.customer_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Recent Activity */}
      <Grid container spacing={3}>
        {/* Recent Tenants */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">{t('dashboard.recentTenants')}</Typography>
                <Button 
                  variant="text" 
                  onClick={() => navigate('/tenants')}
                  size="small"
                >
                  {t('common.view')} {t('common.all', { defaultValue: 'All' })}
                </Button>
              </Box>

              {recentTenants.length === 0 ? (
                <Typography color="text.secondary" align="center" py={2}>
                  {t('dashboard.noRecentTenants')}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('dashboard.name')}</TableCell>
                        <TableCell>{t('dashboard.status')}</TableCell>
                        <TableCell>{t('dashboard.created')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentTenants.slice(0, 5).map((tenant) => (
                        <TableRow 
                          key={tenant.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/tenants/${tenant.id}`)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {tenant.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={tenant.status} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {format(new Date(tenant.created_at), 'MMM dd, yyyy')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Deployments */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">{t('dashboard.recentDeployments')}</Typography>
                <Button 
                  variant="text" 
                  onClick={() => navigate('/deployments')}
                  size="small"
                >
                  {t('common.view')} {t('common.all', { defaultValue: 'All' })}
                </Button>
              </Box>

              {recentDeployments.length === 0 ? (
                <Typography color="text.secondary" align="center" py={2}>
                  {t('dashboard.noRecentDeployments')}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('dashboard.operation')}</TableCell>
                        <TableCell>{t('dashboard.status')}</TableCell>
                        <TableCell>{t('dashboard.started')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentDeployments.slice(0, 5).map((deployment) => (
                        <TableRow key={deployment.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {deployment.operation_type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={deployment.status} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {format(new Date(deployment.started_at), 'MMM dd, HH:mm')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('dashboard.quickActions')}
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              onClick={() => navigate('/tenants')}
              startIcon={<Business />}
            >
              {t('dashboard.createNewTenant')}
            </Button>
            {user?.role === 'super_admin' && (
              <Button
                variant="outlined"
                onClick={() => navigate('/users')}
              >
                {t('dashboard.manageResellers')}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardPage;